import type { PageSeoCopy, SeoLocale, SeoPageKey } from "./seoTypes";
import { SITE_LAYOUT } from "@/lib/site/hosts";
import {
  GUIDE_PAGE_KEYS,
  isGuidePageKey,
  PUBLIC_PAGE_KEYS,
  publicPath,
  type GuidePageKey,
  type PublicPageKey,
} from "@/lib/site/publicPages";
import { getGuide } from "@/lib/guides";
import { pick } from "@/lib/landing/locale";

/**
 * Indexed marketing/legal URLs: these get prerendered, listed in the sitemap and
 * linked with hreflang.
 */
export const INDEXED_PAGE_KEYS: readonly SeoPageKey[] = [
  "landing",
  "privacy",
  "dpa",
  "terms",
  "cookies",
  "contact",
  "changelog",
  ...GUIDE_PAGE_KEYS,
] as const;

export function isIndexedPageKey(key: SeoPageKey): boolean {
  return (INDEXED_PAGE_KEYS as readonly string[]).includes(key);
}

const SEO: Record<Exclude<SeoPageKey, GuidePageKey>, Record<SeoLocale, PageSeoCopy>> = {
  landing: {
    en: {
      title: "Zettl – match Fio bank payments with receipts",
      description:
        "Zettl connects your Fio banka account via API and looks for each payment’s receipt or invoice automatically. For Czech companies and freelancers.",
    },
    cs: {
      title: "Zettl – párování plateb z Fio banky s doklady",
      description:
        "Zettl napojí účet u Fio banky přes API a ke každé platbě sám hledá účtenku nebo fakturu. Pro s.r.o. a OSVČ. Doklady pro účetní bez dohledávání.",
    },
  },
  privacy: {
    en: {
      title: "Privacy policy — Zettl",
      description:
        "How Zettl processes personal data, legal bases, retention, and your rights under GDPR. Available in Czech and English.",
    },
    cs: {
      title: "Zásady ochrany osobních údajů — Zettl",
      description:
        "Jak Zettl zpracovává osobní údaje, právní základy, dobu uchovávání a vaše práva podle GDPR.",
    },
  },
  dpa: {
    en: {
      title: "Data processing agreement — Zettl",
      description:
        "Processor obligations, sub-processors, security measures, and GDPR Article 28 terms for organizations using Zettl.",
    },
    cs: {
      title: "Smlouva o zpracování osobních údajů — Zettl",
      description:
        "Povinnosti zpracovatele, dílčí zpracovatelé, bezpečnostní opatření a čl. 28 GDPR pro organizace používající Zettl.",
    },
  },
  terms: {
    en: {
      title: "Terms of service — Zettl",
      description:
        "Terms governing use of the Zettl service, accounts, acceptable use, liability, and governing law.",
    },
    cs: {
      title: "Obchodní podmínky — Zettl",
      description:
        "Podmínky používání služby Zettl, účty, přijatelné použití, odpovědnost a rozhodné právo.",
    },
  },
  cookies: {
    en: {
      title: "Cookie policy — Zettl",
      description:
        "Zettl uses essential cookies for security and sign-in, and analytics cookies (Google Analytics 4, PostHog) only after you agree in the cookie banner. No advertising cookies.",
    },
    cs: {
      title: "Zásady cookies — Zettl",
      description:
        "Zettl používá nezbytné cookies pro bezpečnost a přihlášení a analytické cookies (Google Analytics 4, PostHog) jen s vaším souhlasem v cookie liště. Žádné reklamní cookies.",
    },
  },
  contact: {
    en: {
      title: "Contact — Zettl",
      description:
        "Reach Zettl for product support, technical questions, or GDPR requests. We respond as soon as we can.",
    },
    cs: {
      title: "Kontakt — Zettl",
      description:
        "Napište Zettl ohledně podpory produktu, technických dotazů nebo žádostí podle GDPR. Ozveme se, jakmile to půjde.",
    },
  },
  changelog: {
    en: {
      title: "What's new — Zettl",
      description:
        "Zettl's version history: new features, improvements and fixes, release by release.",
    },
    cs: {
      title: "Novinky — Zettl",
      description: "Historie verzí Zettlu: nové funkce, vylepšení a opravy, vydání po vydání.",
    },
  },
  auth: {
    en: {
      title: "Sign in — Zettl",
      description: "Secure sign-in to your Zettl account.",
    },
    cs: {
      title: "Přihlášení — Zettl",
      description: "Bezpečné přihlášení k účtu Zettl.",
    },
  },
  resetPassword: {
    en: {
      title: "Reset password — Zettl",
      description: "Set a new password for your Zettl account.",
    },
    cs: {
      title: "Obnovení hesla — Zettl",
      description: "Nastavení nového hesla k účtu Zettl.",
    },
  },
  app: {
    en: {
      title: "Zettl — Finance dashboard",
      description:
        "Manage transactions, invoices, and insights in your Zettl workspace. Sign in required.",
    },
    cs: {
      title: "Zettl — Finanční přehled",
      description:
        "Spravujte transakce, faktury a přehledy v pracovním prostoru Zettl. Vyžaduje přihlášení.",
    },
  },
  notFound: {
    en: {
      title: "Page not found — Zettl",
      description: "The page you requested does not exist or has been moved.",
    },
    cs: {
      title: "Stránka nenalezena — Zettl",
      description: "Požadovaná stránka neexistuje nebo byla přesunuta.",
    },
  },
};

/** The guide hub; each guide's own copy lives with its content (`src/lib/guides/`). */
const GUIDES_HUB: Record<SeoLocale, PageSeoCopy> = {
  cs: {
    title: "Průvodce: platby z Fio, doklady a podklady pro účetní — Zettl",
    description:
      "Jak Zettl stahuje platby z Fio banky, páruje je s účtenkami a fakturami a připraví podklady pro účetní. Návody krok za krokem.",
  },
  en: {
    title: "Guides: Fio payments, documents and files for your accountant — Zettl",
    description:
      "How Zettl downloads your Fio banka payments, matches them with receipts and invoices and prepares the files for your accountant, step by step.",
  },
};

/** The free tools hub. */
const TOOLS_HUB: Record<SeoLocale, PageSeoCopy> = {
  cs: {
    title: "Nástroje zdarma pro bankovní výpisy a účetnictví, v prohlížeči — Zettl",
    description:
      "Bezplatné nástroje, které běží jen ve vašem prohlížeči: soubor se nikam neodesílá a nepotřebujete registraci. Převod výpisu GPC / ABO do CSV a Excelu.",
  },
  en: {
    title: "Free tools for bank statements and bookkeeping, in your browser — Zettl",
    description:
      "Free tools that run only in your browser: the file is never sent anywhere and no sign-up is needed. Convert a GPC / ABO statement to CSV and Excel.",
  },
};

export function getPageSeo(pageKey: SeoPageKey, locale: SeoLocale): PageSeoCopy {
  if (pageKey === "guides") return GUIDES_HUB[locale];
  if (pageKey === "tools") return TOOLS_HUB[locale];
  if (isGuidePageKey(pageKey)) {
    // Plain text for <title> and meta: the no-break spaces `pick` adds are fine there.
    const { seo } = getGuide(pageKey);
    return { title: pick(seo.title, locale), description: pick(seo.description, locale) };
  }
  return SEO[pageKey][locale];
}

/**
 * Canonical path of an indexed page (hreflang, sitemap, canonical) in the
 * layout the site is indexed under (`SITE_LAYOUT`); `null` for everything else.
 */
export function indexedPathFor(pageKey: SeoPageKey, lang: SeoLocale): string | null {
  return isPublicPageKey(pageKey) ? publicPath(pageKey, lang, SITE_LAYOUT) : null;
}

function isPublicPageKey(key: SeoPageKey): key is PublicPageKey {
  return (PUBLIC_PAGE_KEYS as readonly string[]).includes(key);
}
